/**
 * A semana da mãe: sete dias numa tela, para ela ver que a semana cabe.
 *
 * **Agora ela também põe e marca.** "Não consigo lançar atividades nos dias",
 * "quero poder marcar como realizado cada uma das atividades": a grade
 * desenhava sete dias sem nenhum jeito de escrever num deles, e sem nenhum
 * jeito de dizer "isso já aconteceu".
 *
 * **A marca é memória dela, não boletim meu** — e é essa linha que segura a
 * regra 2. Mostrar o que a mãe **marcou** é lembrar por ela. Transformar o que
 * ela **não marcou** em juízo seria medi-la. Por isso dia sem marca não é dia
 * falhado, é dia sem resposta, e continua sem resposta para sempre, se ela
 * quiser: nada aqui diz "pendente", "atrasado" nem "não feito", nada risca uma
 * linha, nada soma, conta ou compara dias. Muda de aparência só a linha em que
 * ela mesma tocou.
 *
 * Bloco protegido não se cumpre — ele acontece. Nem se oferece a marca nem se
 * desenha, para ninguém ler "faltei ao meu tempo com as crianças".
 *
 * **E tudo aqui pode ser consertado.** É a regra 7 do CLAUDE.md, e ela nasceu
 * desta tela: o Carlos abriu a Semana dele e viu jiu-jitsu num domingo que ele
 * nunca pediu, "levar as crianças para a escola" no sábado, e o mesmo bloco
 * repetido sete vezes — sem nada para tocar. Mandar a mãe procurar outra tela
 * para consertar o que ela está vendo é a mesma coisa que não ter conserto.
 *
 * Tocar numa linha abre o conserto do que ela é: o bloco do dia, o
 * compromisso, ou o que fazer com a tarefa de hoje. Os dois formulários são os
 * mesmos das outras telas — vêm de `componentes/`, para não existirem duas
 * versões do mesmo conserto divergindo em silêncio.
 *
 * Medidas de docs/design/Semana.dc.html.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react'

import {
  api,
  deslocar,
  fraseDoErro,
  dataPorExtenso,
  diaDaSemana,
  hhmm,
  intervaloPorExtenso,
  type BlocoDoDia,
  type CompromissoMarcado,
  type CompromissoNovo,
  type CorrecaoDeBloco,
  type CorrecaoDeCompromisso,
  type ItemDaSemana,
  type Semana as SemanaDaCasa,
} from '../api'
import { FormularioDoBloco } from '../componentes/BlocoDoDia'
import { CorrecaoDoCompromisso } from '../componentes/CorrecaoDoCompromisso'
import { Icone } from '../componentes/Icone'

/**
 * `aoAjustarODia` é a porta para onde ela mexe nas horas fixas do dia.
 *
 * Opcional porque quem navega é o `App.tsx`, e sem ele o botão não aparece:
 * botão que não leva a lugar nenhum é pior do que a frase que ele substituiu.
 */
export function Semana({ aoAjustarODia }: { aoAjustarODia?: () => void }) {
  const [semana, setSemana] = useState<SemanaDaCasa | null>(null)
  /**
   * Qual semana está aberta. `null` é a de hoje.
   *
   * "Posso querer ver o histórico, a agenda nos dias anteriores." A tela
   * mostrava sete dias e mais nada: o que passou sumia e o que vinha não
   * existia — nem para conferir se aquela consulta era mesmo quinta.
   */
  const [qual, setQual] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  /** Qual linha está aberta para conserto. Uma de cada vez. */
  const [aberto, setAberto] = useState<string | null>(null)
  /**
   * Em que dia ela está pondo uma coisa. A data, porque é a data que o
   * formulário já traz preenchida: ela está olhando o sábado 12, e digitar
   * "12" noutra tela é o trabalho que a Ruth existe para tirar.
   */
  const [pondo, setPondo] = useState<string | null>(null)
  /**
   * O bloco e o compromisso inteiros, buscados só quando ela toca.
   *
   * A linha da semana carrega o nome, a hora e a identidade — não os dias nem a
   * recorrência. Montar um objeto pela metade com o que a linha tem faria o
   * formulário abrir dizendo que a aula não se repete, e ela salvaria isso.
   * Buscar de verdade custa uma chamada, e só na hora em que ela toca.
   */
  const [blocos, setBlocos] = useState<BlocoDoDia[] | null>(null)
  const [marcados, setMarcados] = useState<CompromissoMarcado[] | null>(null)
  const [buscando, setBuscando] = useState(false)

  const carregar = useCallback(async () => {
    try {
      setSemana(await api.semana(qual ?? undefined))
      setErro(null)
    } catch (e) {
      setErro(fraseDoErro(e, 'não consegui abrir sua semana'))
    }
  }, [qual])

  useEffect(() => {
    void carregar()
  }, [carregar])

  /**
   * Consertar aqui muda a semana inteira: tirar o sábado de um bloco apaga sete
   * linhas, mudar a hora de uma série move todas. Em vez de adivinhar o
   * resultado, a tela recarrega e mostra o que o servidor diz.
   */
  const depoisDeMexer = useCallback(
    async (chamada: Promise<unknown>) => {
      try {
        await chamada
        setAberto(null)
        setErro(null)
        // As listas inteiras vão junto. Elas são buscadas uma vez e ficavam
        // aqui para sempre: depois de apagar um bloco, a grade recarregava e a
        // cópia velha continuava em memória, com o bloco apagado dentro. O
        // conserto seguinte abria com um bloco que já não existia, e o "Pode
        // tirar" voltava "não achei esse bloco". Zerar custa uma busca; não
        // zerar custa a confiança dela na tela.
        setBlocos(null)
        setMarcados(null)
        await carregar()
      } catch (e) {
        setErro(fraseDoErro(e, 'não consegui mexer nisso agora'))
      }
    },
    [carregar],
  )

  /**
   * O que ela pôs volta pelo servidor, como todo o resto desta tela.
   *
   * Um compromisso novo pode cair num bloco protegido, virar linha em mais de
   * um dia, mudar o resumo da semana. Nada disso a tela sabe adivinhar — e
   * inventar a linha na hora faria a semana mostrar uma coisa e o servidor
   * guardar outra.
   */
  const acrescentar = useCallback(
    async (novo: CompromissoNovo) => {
      await api.marcarCompromisso(novo)
      setPondo(null)
      setErro(null)
      // A lista de compromissos também envelheceu: sem isto, tocar no que ela
      // acabou de pôr não acharia o compromisso e a tela diria que não
      // conseguiu abrir — logo depois de ele ter sido criado com sucesso.
      setMarcados(null)
      await carregar()
    },
    [carregar],
  )

  const abrir = useCallback(
    async (chave: string, tipo: ItemDaSemana['tipo']) => {
      // Nunca os dois abertos: o conserto e o formulário de pôr abrem no mesmo
      // lugar, e um embaixo do outro ela não sabe mais o que está mexendo.
      setPondo(null)
      setAberto(chave)
      setBuscando(true)
      try {
        if (tipo === 'protegido' && blocos === null)
          setBlocos((await api.configuracoes()).blocos)
        if (tipo === 'compromisso' && marcados === null)
          setMarcados((await api.compromissos()).compromissos)
      } catch {
        // Sem a lista, o conserto daquele tipo não abre — e a frase embaixo da
        // linha diz por onde ir, em vez de girar para sempre.
      } finally {
        setBuscando(false)
      }
    },
    [blocos, marcados],
  )

  return (
    <>
      <header className="cabeca">
        <div className="cabeca__datas">
          <h1 className="cabeca__titulo">Semana</h1>
          <span className="cabeca__data">
            {semana ? intervaloPorExtenso(semana.inicio, semana.fim) : ' '}
          </span>
        </div>
      </header>

      {semana && (
        <div className="semana__navegar">
          <button
            type="button"
            className="semana__passo"
            aria-label="Semana anterior"
            onClick={() => setQual(deslocar(semana.inicio, -7))}
          >
            <Icone nome="voltar" tamanho={18} />
          </button>

          {qual ? (
            <button type="button" className="semana__hoje" onClick={() => setQual(null)}>
              Voltar pra esta semana
            </button>
          ) : (
            <span className="semana__hoje semana__hoje--parada">esta semana</span>
          )}

          <button
            type="button"
            className="semana__passo semana__passo--frente"
            aria-label="Próxima semana"
            onClick={() => setQual(deslocar(semana.inicio, 7))}
          >
            <Icone nome="voltar" tamanho={18} />
          </button>
        </div>
      )}

      {semana && <p className="frase-do-dia">{semana.resumo}</p>}

      <div className="corpo">
        {/* O dia cheio de ponta a ponta é a única coisa desta tela que não se
            conserta na linha: o buraco que falta não está em nenhum dos sete
            dias, está nas horas fixas dela. Então o conserto vira botão, aqui,
            embaixo da frase que conta o que houve — antes disso a frase mandava
            ela procurar outra tela, que é a mesma coisa que não ter conserto. */}
        {semana?.sem_espaco && aoAjustarODia && (
          <button type="button" className="botao-primario semana__ajustar" onClick={aoAjustarODia}>
            Ajustar o seu dia
          </button>
        )}

        {erro && <p className="vazio">{erro}</p>}

        {semana?.dias.map((dia) => (
          <Dia
            key={dia.data}
            data={dia.data}
            itens={dia.itens}
            hoje={dia.data === semana.hoje}
            passou={dia.data < semana.hoje}
            futuro={dia.data > semana.hoje}
            aberto={aberto}
            blocos={blocos}
            marcados={marcados}
            buscando={buscando}
            pondo={pondo === dia.data}
            aoAbrir={abrir}
            aoFechar={() => setAberto(null)}
            aoMexer={depoisDeMexer}
            aoPor={() => {
              // Fecha o conserto que estivesse aberto: dois formulários no
              // mesmo dia é a tela pedindo para ela escolher qual é qual.
              setAberto(null)
              setPondo(dia.data)
            }}
            aoDesistirDePor={() => setPondo(null)}
            aoAcrescentar={acrescentar}
          />
        ))}
      </div>
    </>
  )
}

type Mexer = (chamada: Promise<unknown>) => Promise<void>

function Dia({
  data,
  itens,
  hoje,
  passou,
  futuro,
  aberto,
  blocos,
  marcados,
  buscando,
  pondo,
  aoAbrir,
  aoFechar,
  aoMexer,
  aoPor,
  aoDesistirDePor,
  aoAcrescentar,
}: {
  data: string
  itens: ItemDaSemana[]
  hoje: boolean
  /** O dia já aconteceu. Marcar continua valendo; convidar a criar, não. */
  passou: boolean
  /** O dia ainda não chegou. Não se diz "fui" ao que não aconteceu. */
  futuro: boolean
  aberto: string | null
  blocos: BlocoDoDia[] | null
  marcados: CompromissoMarcado[] | null
  buscando: boolean
  pondo: boolean
  aoAbrir: (chave: string, tipo: ItemDaSemana['tipo']) => void
  aoFechar: () => void
  aoMexer: Mexer
  aoPor: () => void
  aoDesistirDePor: () => void
  aoAcrescentar: (novo: CompromissoNovo) => Promise<void>
}) {
  const [sigla, numero] = diaDaSemana(data)

  return (
    <div className="semana__dia">
      <div className="semana__marca">
        <span className={`semana__sigla ${hoje ? 'semana__sigla--hoje' : ''}`}>{sigla}</span>
        <span className={`semana__numero display ${hoje ? 'semana__numero--hoje' : ''}`}>
          {numero}
        </span>
      </div>

      <div className="semana__itens">
        {itens.map((item, i) => {
          // A chave junta o dia porque uma série repetida tem o mesmo id em
          // vários dias: sem o dia, tocar na terça abriria também a quinta.
          const chave = `${data}-${item.id}-${i}`
          return (
            <div key={chave}>
              <Item item={item} aoTocar={() => void aoAbrir(chave, item.tipo)} />
              {aberto === chave && (
                <Conserto
                  item={item}
                  data={data}
                  futuro={futuro}
                  bloco={blocos?.find((b) => b.id === item.id) ?? null}
                  marcado={marcados?.find((c) => c.id === item.id) ?? null}
                  buscando={buscando}
                  aoFechar={aoFechar}
                  aoMexer={aoMexer}
                />
              )}
            </div>
          )
        })}

        {/* Marcar que aconteceu vale no passado — é para isso que ela volta aqui.
            Convidar a **pôr** uma coisa num dia que já foi, não: a tela chegou a
            oferecer "marcar uma coisa" numa segunda de agosto. Quem quiser
            registrar algo que passou tem a tela Compromissos, com o campo de
            data, e tem a conversa. */}
        {pondo ? (
          <Acrescentar data={data} aoAcrescentar={aoAcrescentar} aoFechar={aoDesistirDePor} />
        ) : (
          !passou && <Por data={data} vazio={itens.length === 0} aoPor={aoPor} />
        )}
      </div>
    </div>
  )
}

/**
 * Marcar uma coisa **naquele** dia — e o tamanho dele diz onde a mãe está olhando.
 *
 * O lugar é o mesmo nos sete dias: o fim da lista do dia, porque é ali que a
 * leitura de cima para baixo termina e "e mais uma" é a pergunta seguinte. O
 * que muda é o volume.
 *
 * **No dia vazio ele fala.** O espaço já está reservado e em branco (a grade
 * guarda a altura de uma linha em todo dia), então o convite escrito não
 * empurra nada para baixo — e é exatamente ali que ela precisa dele: o sábado
 * vazio é o dia que ela olha sem ter onde escrever.
 *
 * **No dia que já tem coisas ele cala.** Sobra o "+", encostado na direita, sem
 * palavra nenhuma: o olho lê os nomes pela esquerda, e nada atravessa essa
 * coluna. Sete convites escritos empurrariam a semana para fora da tela, e a
 * semana inteira numa tela só é o produto.
 */
function Por({ data, vazio, aoPor }: { data: string; vazio: boolean; aoPor: () => void }) {
  return (
    <button
      type="button"
      className={`semana__por ${vazio ? 'semana__por--vazio' : ''}`}
      // O dia vai no rótulo mesmo quando está escrito na tela: "Marcar uma coisa
      // aqui" repetido quatro vezes não diz a ninguém qual é qual.
      aria-label={`Marcar uma coisa em ${dataPorExtenso(data)}`}
      onClick={aoPor}
    >
      <Icone nome="somar" tamanho={16} />
      {vazio && 'Marcar uma coisa aqui'}
    </button>
  )
}

/**
 * O formulário de pôr, aberto no próprio dia.
 *
 * **A data não é campo, é o título.** Ela tocou no sábado 12; perguntar de novo
 * que dia é seria devolver para ela o trabalho que o toque já fez. Três coisas
 * e acabou — o que é, a hora, o lugar. Repetição e lembrete ficam na tela de
 * Compromissos: pôr no meio da semana é pressa, e pressa não marca caixinha.
 */
function Acrescentar({
  data,
  aoAcrescentar,
  aoFechar,
}: {
  data: string
  aoAcrescentar: (novo: CompromissoNovo) => Promise<void>
  aoFechar: () => void
}) {
  const [titulo, setTitulo] = useState('')
  const [hora, setHora] = useState('09:00')
  const [local, setLocal] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const pronto = titulo.trim() !== '' && hora !== ''

  const salvar = async (evento: FormEvent) => {
    evento.preventDefault()
    if (salvando || !pronto) return

    setSalvando(true)
    setErro(null)
    try {
      await aoAcrescentar({
        titulo: titulo.trim(),
        quando: data,
        hora: `${hora}:00`,
        local: local.trim() || null,
      })
    } catch (e) {
      // O formulário continua aberto com tudo dentro: recomeçar de branco
      // depois de a rede cair é a mãe digitando duas vezes a mesma coisa.
      setErro(fraseDoErro(e, 'Não consegui pôr isso agora.'))
      setSalvando(false)
    }
  }

  return (
    <form className="conf-pessoa" onSubmit={salvar}>
      <p className="semana__conserto-nota">{dataPorExtenso(data)}</p>

      <label className="conf-pessoa__campo">
        <span>O que é</span>
        <input
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Pediatra da Ana"
          maxLength={160}
          autoFocus
        />
      </label>

      <label className="conf-pessoa__campo">
        <span>Que hora</span>
        <input type="time" value={hora} onChange={(e) => setHora(e.target.value)} />
      </label>

      <label className="conf-pessoa__campo">
        <span>Onde</span>
        <input
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          placeholder="deixe vazio se não tem lugar"
          maxLength={200}
        />
      </label>

      {erro && (
        <p className="semana__conserto-nota" role="alert">
          {erro}
        </p>
      )}

      <div className="conf-pessoa__acoes">
        <button type="submit" className="botao-primario" disabled={salvando || !pronto}>
          {salvando ? 'Marcando…' : 'Marcar'}
        </button>
        <button type="button" className="conf-pessoa__deixar" onClick={aoFechar}>
          Deixa pra lá
        </button>
      </div>
    </form>
  )
}

const CLASSE: Record<ItemDaSemana['tipo'], string> = {
  tarefa: '',
  protegido: 'semana__item--protegido',
  compromisso: 'semana__item--compromisso',
}

/**
 * Uma linha do dia — o aviso de que ela abre, e a marca que a mãe deixou.
 *
 * "Na página em anexo não consigo editar nada": toda linha daqui já abria o
 * conserto ao toque, e nada na tela dizia isso. Uma linha que parece texto é
 * uma linha que ninguém toca. A seta é a mesma dos itens de Mais, onde ela já
 * aprendeu que seta quer dizer "isto abre".
 *
 * **O que ela marcou troca de ícone, e só.** Um tique no lugar do ícone do tipo
 * — mesma altura, mesma linha, nada riscado. Riscar seria dizer que a linha sem
 * risco é a que falta, e é aí que uma memória vira boletim: quem não recebeu
 * marca nenhuma continua exatamente como sempre foi.
 */
function Item({ item, aoTocar }: { item: ItemDaSemana; aoTocar: () => void }) {
  // Bloco não se cumpre, ele acontece — e ainda que o servidor mande a marca,
  // ela não aparece aqui: ninguém precisa ver "fui" no tempo com as crianças.
  const marcado = item.cumprido && item.tipo !== 'protegido'
  // O tique é a única coisa que o leitor de tela não enxerga: sem isto, a linha
  // marcada e a não marcada são a mesma frase para quem ouve a tela.
  const dito = item.tipo === 'compromisso' ? ', você marcou que foi' : ', você marcou que fez'

  return (
    <button
      type="button"
      className={`semana__item tocavel ${CLASSE[item.tipo]} ${
        marcado ? 'semana__item--cumprido' : ''
      }`}
      aria-label={`Consertar ${item.nome}${marcado ? dito : ''}`}
      onClick={aoTocar}
    >
      {marcado && <Icone nome="check" tamanho={14} />}
      {!marcado && item.tipo === 'protegido' && <Icone nome="protegido" tamanho={14} />}
      {!marcado && item.tipo === 'compromisso' && <Icone nome="calendario" tamanho={14} />}
      <span className="semana__nome">{item.nome}</span>
      <span className="semana__hora hora">
        {item.fim ? intervalo(item.hora, item.fim) : hhmm(item.hora)}
      </span>
      <span className="tocavel__seta">
        <Icone nome="seta" tamanho={16} />
      </span>
    </button>
  )
}

/**
 * O conserto de uma linha, aberto embaixo dela.
 *
 * Cada tipo tem o seu, e são os mesmos das outras telas. A tarefa é a exceção:
 * o conserto dela não é um formulário, são três decisões que ela toma em um
 * toque — já fiz, hoje não, isso não é da minha casa.
 *
 * Em cima de tudo, no compromisso e na tarefa, vem a marca: "fui", "já fiz". É
 * o que ela abriu a linha para dizer na maior parte das vezes, e o que se faz
 * mais vezes fica mais perto do dedo.
 */
function Conserto({
  item,
  data,
  futuro,
  bloco,
  marcado,
  buscando,
  aoFechar,
  aoMexer,
}: {
  item: ItemDaSemana
  data: string
  /** O dia ainda não chegou. Só o "fui" se importa com isso. */
  futuro: boolean
  bloco: BlocoDoDia | null
  marcado: CompromissoMarcado | null
  buscando: boolean
  aoFechar: () => void
  aoMexer: Mexer
}) {
  if (item.tipo === 'protegido') {
    // Sem o bloco de verdade não abre nada: um formulário com os dias em branco
    // faria ela salvar um bloco que não acontece em dia nenhum.
    if (!bloco) return <Esperando buscando={buscando} />
    return (
      <FormularioDoBloco
        bloco={bloco}
        aoSalvar={(campos: CorrecaoDeBloco) => aoMexer(api.corrigirBloco(bloco.id, campos))}
        aoFechar={aoFechar}
        aoTirar={() => aoMexer(api.tirarBloco(bloco.id))}
      />
    )
  }

  if (item.tipo === 'compromisso') {
    return (
      <>
        {/* Antes da busca, de propósito: dizer "fui" só precisa do dia e do
            compromisso, que a linha já tem. Se a lista não vier, ela perde o
            conserto da série — não pode perder também a marca. */}
        <Fui item={item} data={data} jaFoi={!futuro} aoMexer={aoMexer} />

        {!marcado && <Esperando buscando={buscando} />}

        {marcado && marcado.repete_dias.length > 0 && (
          <div className="semana__conserto">
            <p className="semana__conserto-nota">
              Isso se repete. Se nesse dia não tem — feriado, viagem —, eu tiro só esse dia.
            </p>
            <div className="semana__conserto-acoes">
              <button
                type="button"
                className="conf-pessoa__deixar"
                onClick={() => void aoMexer(api.pularODia(marcado.id, data))}
              >
                Nesse dia não tem
              </button>
            </div>
          </div>
        )}
        {marcado && (
          <CorrecaoDoCompromisso
            compromisso={marcado}
            aoSalvar={(campos: CorrecaoDeCompromisso) =>
              aoMexer(api.corrigirCompromisso(marcado.id, campos))
            }
            aoFechar={aoFechar}
          />
        )}
      </>
    )
  }

  return <DaTarefa item={item} aoFechar={aoFechar} aoMexer={aoMexer} />
}

/**
 * "Fui" — e o jeito de tirar isso, que é metade de poder marcar.
 *
 * Por data, nunca pela série: ter ido na terça não diz nada sobre a quinta.
 *
 * Não existe o botão contrário. "Não fui" seria a Ruth guardando uma falta, e
 * falta é medida; o que existe é **tirar a marca**, que desfaz um toque errado
 * e devolve o dia ao estado de quem não respondeu — que é um estado legítimo,
 * e para sempre, se ela quiser.
 */
function Fui({
  item,
  data,
  jaFoi,
  aoMexer,
}: {
  item: ItemDaSemana
  data: string
  /** O dia já chegou. Não se vai a uma coisa que ainda não aconteceu. */
  jaFoi: boolean
  aoMexer: Mexer
}) {
  if (item.cumprido) {
    return (
      <div className="semana__conserto">
        <p className="semana__conserto-nota">Você marcou que foi.</p>
        <div className="semana__conserto-acoes">
          <button
            type="button"
            className="conf-pessoa__deixar"
            onClick={() => void aoMexer(api.compromissoDescumprido(item.id, data))}
          >
            Tirar a marca
          </button>
        </div>
      </div>
    )
  }

  // Dia que ainda não chegou não recebe "fui": a pergunta não tem resposta
  // possível. "Tirar a marca" continua aparecendo em qualquer dia, porque um
  // toque errado não pode ficar preso — é o ramo de cima.
  if (!jaFoi) return null

  return (
    <div className="semana__conserto">
      <div className="semana__conserto-acoes">
        <button
          type="button"
          className="botao-primario"
          onClick={() => void aoMexer(api.compromissoCumprido(item.id, data))}
        >
          Fui
        </button>
      </div>
    </div>
  )
}

/**
 * "Abrindo…" enquanto a busca corre, e uma frase quando ela termina sem achar.
 *
 * Um "Abrindo…" que nunca vira nada é a tela travada em silêncio: ela toca,
 * espera, e desiste sem saber se o problema é dela.
 */
function Esperando({ buscando }: { buscando: boolean }) {
  return (
    <p className="semana__conserto-nota" role="status">
      {buscando
        ? 'Abrindo…'
        : 'Não consegui abrir esse aqui agora. Dá pra mexer nele em Mais → Ajustar a Ruth.'}
    </p>
  )
}

/**
 * O que fazer com uma coisa da casa, sem sair da semana.
 *
 * "Tirar do plano" é a única das três que muda a casa e não só o dia — por isso
 * é a última, e a frase diz o que acontece: para de aparecer, e não fica
 * marcada como não feita, porque nada mede a mãe.
 */
function DaTarefa({
  item,
  aoFechar,
  aoMexer,
}: {
  item: ItemDaSemana
  aoFechar: () => void
  aoMexer: Mexer
}) {
  const [perguntando, setPerguntando] = useState(false)

  if (perguntando) {
    return (
      <div className="semana__conserto">
        <p className="semana__conserto-nota">
          Tiro {item.nome} do plano da casa. Para de aparecer, e não fica marcada como nada.
        </p>
        <div className="semana__conserto-acoes">
          <button
            type="button"
            className="conf-pessoa__tirar"
            onClick={() => item.tarefa_id && void aoMexer(api.virarAChave(item.tarefa_id, false))}
          >
            Pode tirar
          </button>
          <button
            type="button"
            className="conf-pessoa__deixar"
            onClick={() => setPerguntando(false)}
          >
            Deixa aí
          </button>
        </div>
      </div>
    )
  }

  // Marcada, ela não recebe "Já fiz" de novo nem "Hoje não" — as duas
  // perguntas já foram respondidas —, mas recebe o desfazer: o compromisso
  // sempre teve "tirar a marca", e a tarefa era a única marca do app sem volta.
  // Um toque errado num dia antigo não pode ficar preso.
  // As duas
  // perguntariam uma coisa que ela já respondeu. Sobra o que continua valendo —
  // tirar do plano da casa aquilo que nunca foi desta casa.
  if (item.cumprido) {
    return (
      <div className="semana__conserto">
        <p className="semana__conserto-nota">Você marcou que fez.</p>
        <div className="semana__conserto-acoes">
          <button
            type="button"
            className="conf-pessoa__deixar"
            onClick={() => void aoMexer(api.desmarcarFeito(item.id))}
          >
            Tirar a marca
          </button>
          <button type="button" className="conf-pessoa__deixar" onClick={aoFechar}>
            Fechar
          </button>
        </div>
        <button
          type="button"
          className="conf-pessoa__tirar-link"
          onClick={() => setPerguntando(true)}
        >
          Isso não é da minha casa
        </button>
      </div>
    )
  }

  return (
    <div className="semana__conserto">
      <div className="semana__conserto-acoes">
        <button
          type="button"
          className="botao-primario"
          onClick={() => void aoMexer(api.marcarFeito(item.id))}
        >
          Já fiz
        </button>
        <button
          type="button"
          className="conf-pessoa__deixar"
          onClick={() => void aoMexer(api.maisTarde(item.id))}
        >
          Hoje não
        </button>
        <button type="button" className="conf-pessoa__deixar" onClick={aoFechar}>
          Fechar
        </button>
      </div>
      <button
        type="button"
        className="conf-pessoa__tirar-link"
        onClick={() => setPerguntando(true)}
      >
        Isso não é da minha casa
      </button>
    </div>
  )
}


/** "14h" e "17h" viram "14–17h"; com minuto quebrado, os dois aparecem inteiros. */
function intervalo(inicio: string, fim: string): string {
  const comeco = hhmm(inicio)
  const termino = hhmm(fim)
  return comeco.endsWith('h') && termino.endsWith('h')
    ? `${comeco.slice(0, -1)}–${termino}`
    : `${comeco}–${termino}`
}
